/**
 * point-association-api.service.ts - API client for point-network associations
 *
 * @remarks
 * Proporciona métodos para gestionar asociaciones entre entidades punto
 * y la red canónica.
 *
 * @see ARQUITECTURA_ASOCIACION_PUNTO_RED.md
 */

// API_URL ya incluye /api/v1, NO agregar /v1 de nuevo en las URLs
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

/**
 * Obtiene el token de autenticación del localStorage
 */
const getAuthToken = (): string | null => {
  const authStorage = localStorage.getItem('auth-storage');
  if (!authStorage) return null;
  const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
  return parsed.state?.token || null;
};

/**
 * Tipo de asociación
 */
export type AssociationType = 'edge' | 'node' | 'none';

/**
 * Estado de la asociación
 */
export type AssociationStatus =
  | 'valid'
  | 'needs_recalculation'
  | 'recalculated'
  | 'manual_lock'
  | 'orphaned'
  | 'weak'
  | 'needs_review';

/**
 * Método de asociación
 */
export type AssociationMethod = 'automatic' | 'manual';
export type ReviewSource =
  | 'network_change'
  | 'edge_split'
  | 'edge_merge'
  | 'recalculation'
  | 'manual_review'
  | 'building_ingestion';
export type ReviewResolutionAction =
  | 'accepted_suggestion'
  | 'kept_current'
  | 'resolved_manually'
  | 'discarded_suggestion';

/**
 * Tipo de proyección geométrica
 */
export type ProjectionType =
  | 'orthogonal_inside'
  | 'clamped_to_start'
  | 'clamped_to_end';

/**
 * Lado del edge
 */
export type SideOfEdge = 'left' | 'right' | 'center' | 'unknown';

export interface NetworkAssociationTarget {
  type: AssociationType;
  id: string | null;
}

/**
 * Asociación punto-red
 */
export interface NetworkPointAssociation {
  id: string;
  entityType: string;
  entityId: string;
  projectId: string | null;
  associationType: AssociationType;
  target: NetworkAssociationTarget;
  edgeId: string | null;
  nodeId: string | null;
  positionOnEdge: number | null;
  distanceToNetwork: number | null;
  projectedCoordinate: [number, number] | null;
  sideOfEdge: SideOfEdge | null;
  // Campos de proyección y confianza (v4.1)
  projectionType: ProjectionType;
  confidenceScore: number;
  // Estado y método
  status: AssociationStatus;
  method: AssociationMethod;
  networkVersion: number | null;
  createdAt: string;
  updatedAt: string;
  recalculatedAt: string | null;
  // Campos para multiples conexiones
  isPrimary: boolean;
  isLocked: boolean;
  withinThreshold: boolean;
  reviewRequiredAt: string | null;
  reviewSource: ReviewSource | null;
  reviewReason: string | null;
  suggestedTarget: RepairTargetSuggestion | null;
  reviewResolutionAction: ReviewResolutionAction | null;
  reviewResolutionMeta: Record<string, unknown> | null;
  reviewResolvedAt: string | null;
}

export interface RepairTargetSuggestion {
  type: 'edge' | 'node';
  id: string;
  distanceToNetwork: number | null;
  positionOnEdge: number | null;
  projectedCoordinate: [number, number] | null;
  sideOfEdge: SideOfEdge | null;
  projectionType: ProjectionType;
  confidenceScore: number;
  withinThreshold: boolean;
  status: AssociationStatus;
}

/**
 * Candidato para conexión manual
 */
export interface AssociationCandidate {
  type: 'edge' | 'node';
  id: string;
  distance: number;
  positionOnEdge?: number;
  projectedCoordinate?: [number, number];
  sideOfEdge?: SideOfEdge;
  withinThreshold: boolean;
  name?: string;
}

/**
 * Resultado de asociación batch
 */
export interface BatchAssociationResult {
  processed: number;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
}

/**
 * Resultado de recálculo
 */
export interface RecalculationResult {
  total: number;
  recalculated: number;
  failed: number;
}

/**
 * Estadísticas de asociaciones
 */
export interface AssociationStats {
  byEntityType: Record<string, number>;
  byStatus: Record<string, number>;
  byAssociationType: Record<string, number>;
}

export type AssociationReviewState =
  | 'pending_review'
  | 'auto_repairable'
  | 'manual_resolution_required'
  | 'resolved';

export interface AssociationReviewItem {
  associationId: string;
  entityType: string;
  entityId: string;
  module: string;
  entityLabel: string;
  associationType: AssociationType;
  currentTarget: {
    type: AssociationType;
    id: string | null;
  };
  suggestedTarget: RepairTargetSuggestion | null;
  method: AssociationMethod;
  status: AssociationStatus;
  isPrimary: boolean;
  isLocked: boolean;
  distanceToNetwork: number | null;
  reviewState: AssociationReviewState;
  reviewRequiredAt: string | null;
  reviewSource: ReviewSource | null;
  reviewReason: string | null;
  reviewResolvedAt: string | null;
  reviewResolutionAction: ReviewResolutionAction | null;
}

export type PointAssociationRunStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'completed_with_warnings'
  | 'failed';

export interface PointAssociationRun {
  id: string;
  projectId: string;
  operationType: 'associate_batch' | 'recalculate_pending';
  status: PointAssociationRunStatus;
  entityType: string | null;
  bullJobId: string | null;
  progressPct: number;
  scopeMeta: Record<string, unknown> | null;
  resultMeta: Record<string, unknown> | null;
  errorMessage: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RecalculatePendingJobRequest {
  entityType?: string;
  limit?: number;
}

export interface ResolveAssociationReviewRequest {
  action: Extract<ReviewResolutionAction, 'kept_current' | 'resolved_manually' | 'discarded_suggestion'>;
  note?: string;
}

/**
 * Asociación simplificada para vista desde red
 */
export interface AssociationSummary {
  id: string;
  entityType: string;
  entityId: string;
  distance: number | null;
  side?: string | null;
  status: AssociationStatus;
  isPrimary: boolean;
  confidenceScore: number;
}

/**
 * Respuesta de asociaciones de un edge
 */
export interface EdgeAssociationsResponse {
  edgeId: string;
  total: number;
  byEntityType: Record<string, { count: number; label: string }>;
  associations: AssociationSummary[];
}

/**
 * Respuesta de asociaciones de un node
 */
export interface NodeAssociationsResponse {
  nodeId: string;
  total: number;
  byEntityType: Record<string, { count: number; label: string }>;
  associations: AssociationSummary[];
}

/**
 * Cliente API para asociaciones punto-red
 */
export const pointAssociationApi = {
  // ══════════════════════════════════════════════════════════════
  // ASOCIACIÓN INDIVIDUAL
  // ══════════════════════════════════════════════════════════════

  /**
   * Obtener asociación de una entidad
   */
  async getAssociation(
    projectId: string,
    entityType: string,
    entityId: string,
  ): Promise<NetworkPointAssociation | null> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/${entityType}/${entityId}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to get association: ${response.statusText}`);
      }

      // Handle empty body (null response from backend)
      const text = await response.text();
      if (!text || text === 'null') {
        return null;
      }

      return JSON.parse(text);
    } catch (error) {
      console.error('[pointAssociationApi] Error getting association:', error);
      throw error;
    }
  },

  /**
   * Calcular asociación automática
   */
  async calculate(
    projectId: string,
    entityType: string,
    entityId: string,
  ): Promise<NetworkPointAssociation> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/${entityType}/${entityId}/calculate`;

    console.log('[pointAssociationApi.calculate] Calling:', url);
    console.log('[pointAssociationApi.calculate] Token present:', !!token);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    console.log('[pointAssociationApi.calculate] Response status:', response.status);
    console.log('[pointAssociationApi.calculate] Response statusText:', response.statusText);

    if (!response.ok) {
      const errorBody = await response.text();
      console.error('[pointAssociationApi.calculate] Error body:', errorBody);
      throw new Error(`Failed to calculate association: ${response.status} ${response.statusText} - ${errorBody}`);
    }

    return response.json();
  },

  /**
   * Asociación manual
   *
   * @deprecated Usar `addConnection()` en su lugar.
   *
   * Este método está DEPRECADO porque el endpoint backend ignora
   * edgeId/nodeId y solo hace recálculo automático.
   *
   * Para asociar a un target específico, usar:
   * ```typescript
   * await pointAssociationApi.addConnection(
   *   projectId, entityType, entityId,
   *   { type: 'edge', id: edgeId },
   *   coordinates
   * );
   * ```
   *
   * @see addConnection
   */
  async manualAssociate(
    projectId: string,
    entityType: string,
    entityId: string,
    edgeId?: string,
    nodeId?: string,
  ): Promise<NetworkPointAssociation> {
    console.warn(
      '[DEPRECATED] pointAssociationApi.manualAssociate() is deprecated. ' +
      'Use pointAssociationApi.addConnection() instead.'
    );
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/${entityType}/${entityId}/manual`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ edgeId, nodeId }),
    });

    if (!response.ok) {
      throw new Error(`Failed to manual associate: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Eliminar asociación
   */
  async remove(
    projectId: string,
    entityType: string,
    entityId: string,
  ): Promise<void> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/${entityType}/${entityId}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to remove association: ${response.statusText}`);
    }
  },

  /**
   * Bloquear asociación
   */
  async lock(
    projectId: string,
    entityType: string,
    entityId: string,
  ): Promise<NetworkPointAssociation> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/${entityType}/${entityId}/lock`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to lock association: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Desbloquear asociación
   */
  async unlock(
    projectId: string,
    entityType: string,
    entityId: string,
  ): Promise<NetworkPointAssociation> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/${entityType}/${entityId}/unlock`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to unlock association: ${response.statusText}`);
    }

    return response.json();
  },

  // ══════════════════════════════════════════════════════════════
  // MULTIPLES CONEXIONES
  // ══════════════════════════════════════════════════════════════

  /**
   * Obtener TODAS las asociaciones de una entidad
   */
  async getAllAssociations(
    projectId: string,
    entityType: string,
    entityId: string,
  ): Promise<NetworkPointAssociation[]> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/${entityType}/${entityId}/all`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get all associations: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Agregar conexión manual
   */
  async addConnection(
    projectId: string,
    entityType: string,
    entityId: string,
    target: { type: 'edge' | 'node'; id: string },
    coordinates: [number, number],
  ): Promise<NetworkPointAssociation> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/${entityType}/${entityId}/add-connection`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ type: target.type, id: target.id, coordinates }),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Failed to add connection: ${errorBody}`);
    }

    return response.json();
  },

  /**
   * Reemplazar la asociación primaria por un target específico
   */
  async replacePrimaryAssociation(
    projectId: string,
    entityType: string,
    entityId: string,
    target: { type: 'edge' | 'node'; id: string },
    coordinates: [number, number],
  ): Promise<NetworkPointAssociation> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/${entityType}/${entityId}/primary`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ type: target.type, id: target.id, coordinates }),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Failed to replace primary association: ${errorBody}`);
    }

    return response.json();
  },

  /**
   * Eliminar conexión específica
   */
  async removeConnection(
    projectId: string,
    entityType: string,
    entityId: string,
    connectionId: string,
  ): Promise<void> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/${entityType}/${entityId}/${connectionId}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Failed to remove connection: ${errorBody || response.statusText}`);
    }
  },

  /**
   * Establecer conexión como primaria
   */
  async setPrimary(
    projectId: string,
    entityType: string,
    entityId: string,
    connectionId: string,
  ): Promise<NetworkPointAssociation> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/${entityType}/${entityId}/${connectionId}/set-primary`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to set primary: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Bloquear conexión específica
   */
  async lockConnection(
    projectId: string,
    entityType: string,
    entityId: string,
    connectionId: string,
  ): Promise<NetworkPointAssociation> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/${entityType}/${entityId}/${connectionId}/lock`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to lock connection: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Desbloquear conexión específica
   */
  async unlockConnection(
    projectId: string,
    entityType: string,
    entityId: string,
    connectionId: string,
  ): Promise<NetworkPointAssociation> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/${entityType}/${entityId}/${connectionId}/unlock`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to unlock connection: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Obtener candidatos para conexión manual
   */
  async getCandidates(
    projectId: string,
    entityType: string,
    coordinates: [number, number],
    options?: {
      maxDistance?: number;
      includeNodes?: boolean;
    },
  ): Promise<AssociationCandidate[]> {
    const token = getAuthToken();
    const params = new URLSearchParams();
    params.set('lng', coordinates[0].toString());
    params.set('lat', coordinates[1].toString());
    if (options?.maxDistance) params.set('maxDistance', options.maxDistance.toString());
    if (options?.includeNodes) params.set('includeNodes', 'true');

    const url = `${API_URL}/projects/${projectId}/point-associations/${entityType}/dummy/candidates?${params}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get candidates: ${response.statusText}`);
    }

    return response.json();
  },

  // ══════════════════════════════════════════════════════════════
  // BATCH
  // ══════════════════════════════════════════════════════════════

  /**
   * Asociación masiva de todas las entidades de un tipo
   */
  async batchAssociate(
    projectId: string,
    entityType: string,
    forceRecalculate = false,
  ): Promise<BatchAssociationResult> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/${entityType}/batch?forceRecalculate=${forceRecalculate}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to batch associate: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Obtener múltiples asociaciones en un request
   */
  async batchGet(
    projectId: string,
    entityType: string,
    entityIds: string[],
  ): Promise<Record<string, NetworkPointAssociation>> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/${entityType}/batch-get`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ entityIds }),
    });

    if (!response.ok) {
      throw new Error(`Failed to batch get associations: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Recalcular todas las asociaciones pendientes
   */
  async recalculatePending(
    projectId: string,
    entityType?: string,
    limit?: number,
  ): Promise<RecalculationResult> {
    const token = getAuthToken();
    const params = new URLSearchParams();
    if (entityType) params.set('entityType', entityType);
    if (limit) params.set('limit', limit.toString());

    const url = `${API_URL}/projects/${projectId}/point-associations/recalculate-pending?${params}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to recalculate pending: ${response.statusText}`);
    }

    return response.json();
  },

  async listReviewItems(
    projectId: string,
    options?: {
      entityType?: string;
      module?: string;
      state?: 'open' | 'resolved' | 'all';
      limit?: number;
    },
  ): Promise<AssociationReviewItem[]> {
    const token = getAuthToken();
    const params = new URLSearchParams();
    if (options?.entityType) params.set('entityType', options.entityType);
    if (options?.module) params.set('module', options.module);
    if (options?.state) params.set('state', options.state);
    if (options?.limit) params.set('limit', String(options.limit));

    const url = `${API_URL}/projects/${projectId}/point-associations/reviews?${params}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to list review items: ${response.statusText}`);
    }

    return response.json();
  },

  async acceptSuggestedRepair(
    projectId: string,
    associationId: string,
  ): Promise<NetworkPointAssociation> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/reviews/${associationId}/accept-suggestion`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Failed to accept suggested repair: ${errorBody || response.statusText}`);
    }

    return response.json();
  },

  async resolveReview(
    projectId: string,
    associationId: string,
    payload: ResolveAssociationReviewRequest,
  ): Promise<NetworkPointAssociation> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/reviews/${associationId}/resolve`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Failed to resolve review: ${errorBody || response.statusText}`);
    }

    return response.json();
  },

  async enqueueRecalculatePending(
    projectId: string,
    payload: RecalculatePendingJobRequest = {},
  ): Promise<PointAssociationRun> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-association-jobs/recalculate-pending`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Failed to enqueue recalculation: ${errorBody || response.statusText}`);
    }

    return response.json();
  },

  async enqueueAssociateBatch(
    projectId: string,
    entityType: string,
    forceRecalculate = false,
  ): Promise<PointAssociationRun> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-association-jobs/${entityType}/associate-batch?forceRecalculate=${forceRecalculate}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Failed to enqueue batch association: ${errorBody || response.statusText}`);
    }

    return response.json();
  },

  async listRuns(
    projectId: string,
    limit = 20,
  ): Promise<PointAssociationRun[]> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-association-jobs?limit=${limit}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to list association runs: ${response.statusText}`);
    }

    return response.json();
  },

  // ══════════════════════════════════════════════════════════════
  // CONSULTAS
  // ══════════════════════════════════════════════════════════════

  /**
   * Listar asociaciones por tipo de entidad
   */
  async listByType(
    projectId: string,
    entityType: string,
    options?: {
      status?: AssociationStatus;
      edgeId?: string;
      nodeId?: string;
    },
  ): Promise<NetworkPointAssociation[]> {
    const token = getAuthToken();
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.edgeId) params.set('edgeId', options.edgeId);
    if (options?.nodeId) params.set('nodeId', options.nodeId);

    const url = `${API_URL}/projects/${projectId}/point-associations/${entityType}?${params}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to list associations: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Obtener estadísticas de asociaciones
   */
  async getStats(projectId: string): Promise<AssociationStats> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/point-associations/stats`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get stats: ${response.statusText}`);
    }

    return response.json();
  },

  // ══════════════════════════════════════════════════════════════
  // VISTA DESDE LA RED
  // ══════════════════════════════════════════════════════════════

  /**
   * Obtener asociaciones de un edge específico
   */
  async getEdgeAssociations(
    projectId: string,
    edgeId: string,
  ): Promise<EdgeAssociationsResponse> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/network/edges/${edgeId}/associations`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get edge associations: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Obtener asociaciones de un node específico
   */
  async getNodeAssociations(
    projectId: string,
    nodeId: string,
  ): Promise<NodeAssociationsResponse> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/network/nodes/${nodeId}/associations`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get node associations: ${response.statusText}`);
    }

    return response.json();
  },
};

// ══════════════════════════════════════════════════════════════
// MVP WASTE ASSOCIATIONS API
// ══════════════════════════════════════════════════════════════

/**
 * Simple state for MVP (operator-friendly)
 */
export type SimpleAssociationState = 'OK' | 'REVISAR' | 'ERROR';

/**
 * Problem type for grouping
 */
export type ProblemType = 'no_association' | 'distance_warning' | 'distance_error';

/**
 * Individual problem
 */
export interface AssociationProblem {
  entityType: string;
  entityId: string;
  code?: string;
  state: SimpleAssociationState;
  problemType: ProblemType;
  distance: number | null;
  geometry: { type: 'Point'; coordinates: [number, number] };
  edgeId?: string;
  /** Whether the association is locked (confirmed manually) */
  isLocked?: boolean;
}

/**
 * Problems response
 */
export interface ProblemsResponse {
  byType: {
    no_association: AssociationProblem[];
    distance_warning: AssociationProblem[];
    distance_error: AssociationProblem[];
  };
  summary: {
    total: number;
    byState: Record<SimpleAssociationState, number>;
    byEntityType: Record<string, number>;
  };
  meta: {
    projectId: string;
    generatedAt: string;
    thresholds: {
      ok: number;
      revisar: number;
    };
  };
}

/**
 * Coverage color
 */
export type CoverageColor = 'verde' | 'amarillo' | 'rojo';

/**
 * Coverage map response
 */
export interface CoverageMapResponse {
  type: 'FeatureCollection';
  features: {
    type: 'Feature';
    geometry: { type: 'LineString'; coordinates: [number, number][] };
    properties: {
      edgeId: string;
      color: CoverageColor;
      distance: number | null;
      containersNearby: number;
    };
  }[];
  summary: {
    totalEdges: number;
    byColor: Record<CoverageColor, number>;
    totalLengthMeters: Record<CoverageColor, number>;
  };
  meta: {
    projectId: string;
    generatedAt: string;
    thresholds: {
      verde: number;
      amarillo: number;
    };
  };
}

/**
 * Gap cluster
 */
export interface GapCluster {
  id: string;
  edgeIds: string[];
  centroid: [number, number];
  totalLengthMeters: number;
  boundingBox: [number, number, number, number];
}

/**
 * Gaps response
 */
export interface GapsResponse {
  gaps: GapCluster[];
  geojson: {
    type: 'FeatureCollection';
    features: {
      type: 'Feature';
      geometry: { type: 'Point'; coordinates: [number, number] };
      properties: {
        gapId: string;
        edgeCount: number;
        totalLengthMeters: number;
      };
    }[];
  };
  summary: {
    totalGaps: number;
    totalGapLengthMeters: number;
  };
  meta: {
    projectId: string;
    generatedAt: string;
    minEdgeLength: number;
  };
}

/**
 * Location suggestion
 */
export interface LocationSuggestion {
  gapId: string;
  suggestedLocation: [number, number];
  edgeId: string;
  reason: string;
}

/**
 * Suggestions response
 */
export interface SuggestionsResponse {
  suggestions: LocationSuggestion[];
  meta: {
    projectId: string;
    generatedAt: string;
  };
}

/**
 * Entity with simple state for map visualization
 */
export interface EntityWithState {
  type: 'Feature';
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: {
    entityType: string;
    entityId: string;
    code?: string;
    simpleState: SimpleAssociationState;
    distance: number | null;
    isLocked: boolean;
  };
}

/**
 * Entities with states response
 */
export interface EntitiesWithStatesResponse {
  type: 'FeatureCollection';
  features: EntityWithState[];
}

/**
 * Waste Association Analysis API - MVP endpoints
 */
export const wasteAssociationApi = {
  /**
   * GET /waste/associations/problems
   * Returns entities with association problems grouped by type
   */
  async getProblems(projectId: string): Promise<ProblemsResponse> {
    const token = getAuthToken();
    const url = `${API_URL}/waste/associations/problems?projectId=${projectId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get problems: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * GET /waste/associations/coverage-map
   * Returns GeoJSON of edges colored by distance to nearest container
   */
  async getCoverageMap(projectId: string): Promise<CoverageMapResponse> {
    const token = getAuthToken();
    const url = `${API_URL}/waste/associations/coverage-map?projectId=${projectId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get coverage map: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * GET /waste/associations/gaps
   * Detects gaps (clusters of red edges), returns centroids
   */
  async getGaps(projectId: string): Promise<GapsResponse> {
    const token = getAuthToken();
    const url = `${API_URL}/waste/associations/gaps?projectId=${projectId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get gaps: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * GET /waste/associations/suggestions
   * Returns one suggested location per gap
   */
  async getSuggestions(projectId: string): Promise<SuggestionsResponse> {
    const token = getAuthToken();
    const url = `${API_URL}/waste/associations/suggestions?projectId=${projectId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get suggestions: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * GET /waste/associations/entities
   * Returns all Waste entities with simple states for map visualization
   */
  async getEntitiesWithStates(projectId: string): Promise<EntitiesWithStatesResponse> {
    const token = getAuthToken();
    const url = `${API_URL}/waste/associations/entities?projectId=${projectId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get entities with states: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * POST /waste/associations/:entityType/:entityId/confirm
   * Confirms a problematic association - sets manual_lock and marks as valid
   */
  async confirmAssociation(
    entityType: string,
    entityId: string,
  ): Promise<{ success: boolean; message: string }> {
    const token = getAuthToken();
    const url = `${API_URL}/waste/associations/${entityType}/${entityId}/confirm`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to confirm association: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * POST /waste/associations/:entityType/:entityId/ignore
   * Ignores a problematic association - sets manual_lock without recalculating
   */
  async ignoreAssociation(
    entityType: string,
    entityId: string,
  ): Promise<{ success: boolean; message: string }> {
    const token = getAuthToken();
    const url = `${API_URL}/waste/associations/${entityType}/${entityId}/ignore`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to ignore association: ${response.statusText}`);
    }

    return response.json();
  },
};
